<script setup lang="ts">
/**
 * 類似画像モーダル(一括置換・一括削除)。VB版 ConfirmImageListWindow の
 * METHOD_REPLACE / METHOD_DELETE 相当。
 *
 * 基準画像とハッシュ値が完全一致する画像(=実質同一の画像ファイル)をDB横断で
 * 検索し、チェックした複数件を「一括削除」または「新しい1枚で一括置換」する。
 *
 * VB版との違い(Web版ならではの分かりやすさを意識した点):
 *  - VB版はモーダルウィンドウ+DataGridの業務ツール然としたUIだったが、
 *    Web版はカード型のグリッド表示にして、画像を見ながら選べるようにした。
 *  - 「兄弟ASINのみ表示」のON/OFFで検索範囲が変わることを、件数表示で明示する。
 */
import { ref, computed, watch } from 'vue'
import {
  useSearchImagesByHashMutation,
  useBulkUploadStagingUrlMutation,
  useBulkDeleteImagesMutation,
  useBulkReplaceImagesMutation,
} from '@/api/queries'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useResultModalStore } from '@/stores/resultModal'
import type { SimilarImageResult } from '@/types/api'

const props = defineProps<{
  mode: 'replace' | 'delete'
  asin: string
  imageNo: number
  regionCode: string
}>()

const emit = defineEmits<{ close: [] }>()

const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()

const siblingsOnly = ref(true)
const checkedKeys = ref<Set<string>>(new Set())

const searchMutation = useSearchImagesByHashMutation()
const stagingMutation = useBulkUploadStagingUrlMutation()
const deleteMutation = useBulkDeleteImagesMutation()
const replaceMutation = useBulkReplaceImagesMutation()

const results = ref<SimilarImageResult[]>([])
const hashUnavailable = ref(false)

function keyOf(r: SimilarImageResult) {
  return `${r.regionCode}:${r.childAsin}:${r.imageNo}`
}

function runSearch() {
  searchMutation.mutate(
    { asin: props.asin, imageNo: props.imageNo, regionCode: props.regionCode, siblingsOnly: siblingsOnly.value },
    {
      onSuccess: (res) => {
        results.value = res.results
        hashUnavailable.value = res.hashUnavailable ?? false
        // 基準画像は最初からチェックしておく(VB版もChecked=Trueで初期化)
        checkedKeys.value = new Set(res.results.map(keyOf))
      },
      onError: (err) => resultModal.show(`検索に失敗しました: ${(err as Error).message}`, 'ng'),
    },
  )
}
runSearch()
watch(siblingsOnly, runSearch)

function toggleCheck(r: SimilarImageResult) {
  const k = keyOf(r)
  if (checkedKeys.value.has(k)) checkedKeys.value.delete(k)
  else checkedKeys.value.add(k)
  // Setの変更をVueに検知させる
  checkedKeys.value = new Set(checkedKeys.value)
}

function toggleAll() {
  if (checkedKeys.value.size === results.value.length) {
    checkedKeys.value = new Set()
  } else {
    checkedKeys.value = new Set(results.value.map(keyOf))
  }
}

const checkedCount = computed(() => checkedKeys.value.size)

// ---- 置換モード: ファイル選択 ----
const fileInput = ref<HTMLInputElement | null>(null)
const selectedFile = ref<File | null>(null)
const selectedPreviewUrl = ref<string | null>(null)

function pickFile() {
  fileInput.value?.click()
}
function onFileSelected(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (fileInput.value) fileInput.value.value = ''
  if (!file) return
  selectedFile.value = file
  if (selectedPreviewUrl.value) URL.revokeObjectURL(selectedPreviewUrl.value)
  selectedPreviewUrl.value = URL.createObjectURL(file)
}

const isBusy = computed(
  () => searchMutation.isPending.value || stagingMutation.isPending.value ||
        deleteMutation.isPending.value || replaceMutation.isPending.value,
)

async function onExecute() {
  const targets = results.value
    .filter((r) => checkedKeys.value.has(keyOf(r)))
    .map((r) => ({ regionCode: r.regionCode, asin: r.childAsin, imageNo: r.imageNo }))

  if (targets.length === 0) {
    resultModal.show('対象の画像が選択されていません。', 'ng')
    return
  }

  if (props.mode === 'delete') {
    if (!(await confirmModal.ask(`選択した${targets.length}件の画像を削除してよろしいですか？`))) return
    deleteMutation.mutate(
      { targets },
      {
        onSuccess: (res) => {
          resultModal.show(`${res.deletedCount}件の画像を削除しました。`)
          emit('close')
        },
        onError: (err) => resultModal.show(`削除に失敗しました: ${(err as Error).message}`, 'ng'),
      },
    )
  } else {
    if (!selectedFile.value) {
      resultModal.show('置換する画像ファイルを選択してください。', 'ng')
      return
    }
    if (!(await confirmModal.ask(`選択した${targets.length}件の画像を、この新しい画像で置換してよろしいですか？`)))
      return

    try {
      const staging = await stagingMutation.mutateAsync(selectedFile.value.type || 'image/jpeg')
      await fetch(staging.uploadUrl, { method: 'PUT', body: selectedFile.value, headers: { 'Content-Type': staging.contentType } })
      replaceMutation.mutate(
        { stagingKey: staging.stagingKey, contentType: staging.contentType, targets },
        {
          onSuccess: (res) => {
            resultModal.show(`${res.replacedCount}件の画像を置換しました。`)
            emit('close')
          },
          onError: (err) => resultModal.show(`置換に失敗しました: ${(err as Error).message}`, 'ng'),
        },
      )
    } catch (err) {
      resultModal.show(`アップロードに失敗しました: ${(err as Error).message}`, 'ng')
    }
  }
}
</script>

<template>
  <div class="bim-overlay" @click="emit('close')">
    <div class="bim-modal" @click.stop>
      <div class="bim-head">
        <div class="bim-title">
          {{ mode === 'delete' ? '類似画像をまとめて削除' : '類似画像をまとめて置換' }}
        </div>
        <button class="bim-close" @click="emit('close')">
          <svg class="icon" viewBox="0 0 24 24" style="width:18px;height:18px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="bim-sub">
        基準画像(ASIN: {{ asin }} / No.{{ imageNo }})と同一の画像を検索しています。
      </div>

      <label class="chk bim-sibling-chk">
        <input type="checkbox" v-model="siblingsOnly" />
        兄弟ASINのみ表示
        <span class="bim-sibling-hint">(オフにすると全商品からハッシュが一致する画像を検索します)</span>
      </label>

      <div v-if="searchMutation.isPending.value" class="empty-state" style="padding:32px 0;">
        <span class="spinner"></span> 検索中…
      </div>

      <div v-else-if="hashUnavailable" class="empty-state" style="padding:32px 0;">
        この画像にはハッシュ情報が無いため、類似画像を検索できません。
      </div>

      <div v-else-if="results.length === 0" class="empty-state" style="padding:32px 0;">
        一致する画像が見つかりませんでした。
      </div>

      <template v-else>
        <div class="bim-toolbar">
          <button class="btn btn-ghost btn-sm" @click="toggleAll">
            {{ checkedCount === results.length ? '全て解除' : '全て選択' }}
          </button>
          <span class="bim-count">{{ checkedCount }} / {{ results.length }} 件選択中</span>
        </div>

        <div class="bim-grid">
          <label
            v-for="r in results"
            :key="keyOf(r)"
            class="bim-item"
            :class="{ checked: checkedKeys.has(keyOf(r)), basis: r.isBasis }"
          >
            <input type="checkbox" :checked="checkedKeys.has(keyOf(r))" @change="toggleCheck(r)" />
            <img :src="r.imageUrl" :alt="r.childAsin" loading="lazy" />
            <div class="bim-item-info">
              <span class="bim-item-asin">{{ r.childAsin }}<span v-if="r.isBasis" class="bim-basis-tag">基準</span></span>
              <span class="bim-item-no">No.{{ r.imageNo }}</span>
              <span class="bim-item-name" :title="r.productName ?? ''">{{ r.productName || '(商品名未取得)' }}</span>
            </div>
          </label>
        </div>
      </template>

      <div v-if="mode === 'replace' && results.length > 0" class="bim-replace-file">
        <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp,image/gif" style="display:none" @change="onFileSelected" />
        <div class="bim-replace-preview" @click="pickFile">
          <img v-if="selectedPreviewUrl" :src="selectedPreviewUrl" alt="置換後プレビュー" />
          <div v-else class="bim-replace-placeholder">
            <svg class="icon" viewBox="0 0 24 24" style="width:28px;height:28px;"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            <span>クリックして置換後の画像を選択</span>
          </div>
        </div>
      </div>

      <div class="bim-actions">
        <button class="btn btn-ghost" @click="emit('close')">キャンセル</button>
        <button
          class="btn"
          :class="mode === 'delete' ? 'btn-danger' : 'btn-primary'"
          :disabled="isBusy || results.length === 0"
          @click="onExecute"
        >
          {{ isBusy ? '処理中…' : mode === 'delete' ? `${checkedCount}件を削除` : `${checkedCount}件を置換` }}
        </button>
      </div>
    </div>
  </div>
</template>
